/**
 * importChecklistStore — the run state behind the SCRIPT IMPORT CHECKLIST card (owner, 20260914;
 * spec docs/superpowers/specs/2026-09-14-script-import-checklist-design.md §2.1).
 *
 * The card is the finale-panel shape: a small docked rail with one button per thing worth doing
 * after a script import, each button marking itself done once tapped. This store holds nothing but
 * the RUN — which rows exist, which have been tapped, and whether a run is up at all.
 *
 * ROWS RECUT 20260921 (owner, tweak 7): "can we replace the current buttons with the following?
 * Each button should take the user to the Prep tab where the corresponding sub-tab should be
 * selected. - Verify script - Verify cast - Verify locations - Review later" (the tab the owner
 * called Prep that day is the Breakdown tab since 20260922). So the three rows are
 * now one destination each — the Breakdown tab's three sub-tabs — and the `coverage` row (the board's
 * coverage overview) and the old catch-all `verify` row are gone. `reviewChanges` survives on the
 * REVISION variant only, and only because View Changes is a surface none of the owner's four
 * buttons reaches (see ImportChecklistRail's header).
 *
 * IN-MEMORY ONLY, for the same reason guideStore persists nothing: this is per-open guidance, not
 * a preference. Closing the project reloads the page (the normal close path), which drops it; a
 * project SWITCH inside a pooled panel does not, which is why a fresh (non-refresh) project load
 * calls end('projectChanged') from bridge/features/timeline.ts rather than trusting the reload.
 *
 * DELIBERATELY SEPARATE from tutorialStore and guideStore, which it sits beside in the same dock:
 *  · tutorialStore drives the bundled TutorialProject's choreographed TOUR, advancing on native
 *    round-trip acks;
 *  · guideStore drives the example-project GUIDE, advancing on ordinary app state (a route, a play);
 *  · this one advances on nothing at all. Its rows are INDEPENDENT destinations the user may take
 *    in any order, or ignore — there is no step index here and never should be, which is exactly
 *    why merging it into either of those `step` machines would be wrong.
 * The three never share the screen: ImportChecklistRail parks while either of the others is active.
 *
 * IMPORTS NOTHING (cycle rule). uiStore already imports projectStore and tutorialStore, and the
 * checklist card reaches uiStore's latches through `useUiStore.getState()` from the COMPONENT — so
 * keeping this module store-free means the checklist can never close a cycle whichever way a future
 * edit points the arrow. Zustand and nothing else.
 */
import { create } from 'zustand';

/**
 * The tappable rows. 'later' is a dismiss, not a row — it has no done state to record.
 *
 * The three verify* ids are the owner's three buttons (20260921) and each names the Breakdown sub-tab it
 * lands on; they are STABLE TELEMETRY IDS as well as keys here (they ride `step_id` on
 * tutorial_step_advanced), so renaming one splits its funnel. 'reviewChanges' keeps the id it has
 * had since 20260914 for the same reason. The rows it replaced — 'coverage' and the catch-all
 * 'verify' — are retired: nothing emits them any more.
 */
export type ChecklistRow = 'verifyScript' | 'verifyCast' | 'verifyLocations' | 'reviewChanges';

/** 'completed' has had no caller since 20260922 (owner): a card whose last row ticks no longer ends
 *  itself — it relabels its exit "All set!" and waits, so every user-driven end is 'dismissed'. The
 *  reason is kept because end() takes it purely as the CALLER's telemetry dimension and a completed
 *  run that ever wants to say so again should not have to re-invent the word. */
export type ChecklistEndReason = 'completed' | 'dismissed' | 'projectChanged';

const NO_TAPS: Record<ChecklistRow, boolean> = {
  verifyScript: false, verifyCast: false, verifyLocations: false, reviewChanges: false,
};

export interface ImportChecklistState {
  /** Is a run up? False means the card renders nothing at all (as opposed to PARKED, which is the
   *  component's own business — a park keeps this state untouched). */
  active: boolean;
  /**
   * RUN IDENTITY, bumped by every start(). Same job as guideStore's epoch and needed for the same
   * two reasons: the card's first-reveal hold and its `tutorial_step_shown` telemetry dedupe must
   * tell one run from the next, and `active` alone cannot — a second import replaces a live run in
   * ONE commit, with active true on both sides of it.
   */
  epoch: number;
  /** V2+: the project had a prior imported revision at arm time (baseline.versionNumber >= 2), so
   *  the "Review changes from prior revision" row exists. Frozen at arm time on purpose — the row
   *  set must not change under the user because a later repush moved the baseline. */
  revision: boolean;
  /** Done-by-TAP, per row — and since 20260921 that is the ONLY way a row goes done: the one row
   *  that also had a DERIVED done state (coverage, ticked off by a project with nothing pristine
   *  left in it) is gone with the owner's recut, so nothing outside this record decides it. */
  tapped: Record<ChecklistRow, boolean>;
  /**
   * One-shot latch for the REVISION arm, set by bridge/features/screenplay.ts on
   * `reimportComplete { success: true }` and consumed by the post-apply load. `null` = nothing
   * armed; a NUMBER = armed, and the number is the baseline version that was live at arm time.
   *
   * Why a latch rather than arming on the completion message: the revision row and the card's whole
   * V1/V2 shape are computed from `project.baseline`, and at reimportComplete time the store still
   * holds the PRE-apply project — Unity's repush (`setSqlitePath { refresh: true }`) lands a beat
   * later. Arming on the load closes that window (see the reimport reference doc's note on it).
   *
   * Why the latch CARRIES A VERSION rather than being a bare flag (20260914, reviewer): "the next
   * load" is not the same thing as "the post-apply load". A refresh repush can ALREADY BE IN FLIGHT
   * when reimportComplete lands — the cast-bodies-swap repush that follows a fresh open is the
   * routine one — and a bare flag was spent by whichever load resolved first. That load carries the
   * PRE-apply baseline, so it started a V1-shaped run, and the real post-apply repush then found an
   * empty latch and armed nothing at all. Holding the armed version turns the consume's question
   * into "has the baseline actually MOVED since the arm", which only the post-apply load answers
   * yes to.
   */
  armOnNextLoad: number | null;

  /** Start a run, REPLACING whatever was there: taps cleared, epoch bumped, active true. A second
   *  import is a new checklist, never a continuation of the last one. */
  start: (opts: { revision: boolean }) => void;
  markTapped: (row: ChecklistRow) => void;
  /** Arm, recording the baseline version the arm was made against (0 for a project with no import
   *  history yet — a first import into a blank project travels this path and lands on version 1). */
  setArmOnNextLoad: (armedVersion: number) => void;
  /**
   * Read-and-clear, GATED on the baseline having moved: true (and the latch spent) only when
   * `loadedVersion` differs from the version the latch was armed against. An equal version means
   * this load is carrying the pre-apply baseline — an unrelated repush that overtook the apply — so
   * the latch is left standing for the load that does carry the new one.
   */
  consumeArmOnNextLoad: (loadedVersion: number) => boolean;
  /** Drop the latch without arming anything. The FRESH-load half of the bridge's rule: a different
   *  project must never inherit an arm left over from an abandoned reimport in the last one. */
  clearArmOnNextLoad: () => void;
  end: (reason: ChecklistEndReason) => void;
}

export const useImportChecklistStore = create<ImportChecklistState>((set, get) => ({
  active: false,
  epoch: 0,
  revision: false,
  tapped: NO_TAPS,
  armOnNextLoad: null,

  start: ({ revision }) => set({
    active: true,
    epoch: get().epoch + 1,
    revision,
    // A fresh object every run, never a mutation of NO_TAPS — the constant is shared.
    tapped: { ...NO_TAPS },
    // The latch is spent by definition once a run starts: its ONE caller (bridge/features/timeline.ts)
    // has already consumed or dropped it by the time it calls this, and belt-and-braces, a run
    // started by the OTHER arming input (`project_source: 'imported'`) must not leave an arm from
    // some earlier, abandoned reimport standing to re-start the checklist on a later project load.
    armOnNextLoad: null,
  }),

  markTapped: (row) => {
    const { tapped } = get();
    // Skip the no-op write: the rows are re-tappable in the DOM sense only for a beat (a tapped row
    // renders `disabled`), and a second write here would notify every subscriber for nothing.
    if (tapped[row]) return;
    set({ tapped: { ...tapped, [row]: true } });
  },

  setArmOnNextLoad: (armedVersion) => set({ armOnNextLoad: armedVersion }),
  consumeArmOnNextLoad: (loadedVersion) => {
    const armed = get().armOnNextLoad;
    // Read-and-CLEAR, the uiStore latch idiom — but only for a load that MOVED THE BASELINE. Skip
    // the write otherwise: this runs on every project load, an unconditional set() would notify
    // every subscriber each time, and (the reason the guard exists at all) a load still carrying
    // the armed version is a repush that overtook the apply, not the apply's own repush.
    if (armed === null || armed === loadedVersion) return false;
    set({ armOnNextLoad: null });
    return true;
  },
  clearArmOnNextLoad: () => {
    if (get().armOnNextLoad !== null) set({ armOnNextLoad: null });
  },

  end: (reason) => {
    void reason; // the reason is the CALLER's telemetry dimension; the store keeps no history
    // No-op when nothing is running — end('projectChanged') fires on every fresh load, and an
    // unguarded write would notify the card's subscribers on every project open of the user's life.
    if (!get().active) return;
    // `active` ONLY. epoch/revision/tapped are left standing because start() replaces them wholesale
    // anyway, and because clearing them here would make a completed card visibly un-tick its own
    // rows in the same commit it leaves on.
    //
    // armOnNextLoad is deliberately NOT cleared: end('projectChanged') runs BEFORE the load's
    // consume (timeline.ts), so sweeping the latch here would eat the revision arm it exists to
    // carry. Dropping the latch is a SEPARATE decision with a narrower rule — only a FRESH load
    // does it, through clearArmOnNextLoad — and end() has no way to tell the two loads apart.
    set({ active: false });
  },
}));
